import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiUsers, 
  FiTarget, 
  FiAward, 
  FiGlobe,
  FiArrowRight,
  FiTrendingUp,
  FiShield,
  FiZap
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './About.css';

const About = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/about', {
      page_title: 'About LogCrest - Our Mission & Vision',
      page_category: 'about'
    });
  }, []);

  const values = [
    {
      icon: <FiTarget />,
      title: 'Innovation First',
      description: 'We push the boundaries of what\'s possible with AI-powered observability, constantly innovating to solve tomorrow\'s challenges today.'
    },
    {
      icon: <FiUsers />,
      title: 'Customer Success',
      description: 'Your success is our success. We\'re committed to providing exceptional support and ensuring you get maximum value from our platform.'
    },
    {
      icon: <FiShield />,
      title: 'Security & Trust',
      description: 'We take security seriously. Your data is protected with enterprise-grade security measures and compliance standards.'
    },
    {
      icon: <FiZap />,
      title: 'Performance',
      description: 'Speed and reliability are at the core of everything we build. We optimize for performance without compromising on features.'
    }
  ];

  const milestones = [
    {
      year: '2024',
      title: 'LogCrest Founded',
      description: 'Started with a vision to revolutionize observability with AI'
    },
    {
      year: '2024',
      title: 'First AI Models Deployed',
      description: 'Launched our first intelligent monitoring and alerting systems'
    },
    {
      year: '2024',
      title: 'Enterprise Customers',
      description: 'Onboarded our first enterprise customers and achieved SOC 2 compliance'
    },
    {
      year: '2024',
      title: 'Global Expansion',
      description: 'Expanded to serve customers across North America, Europe, and Asia'
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>About LogCrest</h1>
            <p className="hero-description">
              We're on a mission to transform how organizations monitor, analyze, and optimize 
              their infrastructure through intelligent observability solutions powered by AI.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-content">
            <motion.div
              className="mission-text"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2>Our Mission</h2>
              <p>
                To empower organizations with intelligent observability tools that not only 
                monitor their systems but predict, prevent, and resolve issues before they 
                impact users. We believe that with the right AI-powered insights, every 
                organization can achieve unprecedented levels of reliability and performance.
              </p>
              <p>
                Our platform combines cutting-edge machine learning with intuitive design 
                to make complex observability challenges simple and actionable.
              </p>
            </motion.div>
            <motion.div
              className="mission-visual"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="mission-stats">
                <div className="stat">
                  <FiTrendingUp />
                  <span className="stat-number">99.9%</span>
                  <span className="stat-label">Uptime</span>
                </div>
                <div className="stat">
                  <FiUsers />
                  <span className="stat-number">500+</span>
                  <span className="stat-label">Customers</span>
                </div>
                <div className="stat">
                  <FiGlobe />
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Countries</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2>Our Values</h2>
            <p>These core values guide everything we do at LogCrest</p>
          </motion.div>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <div className="value-icon">
                  {value.icon}
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2>Our Journey</h2>
            <p>Key milestones in our mission to revolutionize observability</p>
          </motion.div>
          
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                className="timeline-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                <div className="timeline-marker">
                  <FiAward />
                </div>
                <div className="timeline-content">
                  <div className="timeline-year">{milestone.year}</div>
                  <h3>{milestone.title}</h3>
                  <p>{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2>Ready to join our mission?</h2>
            <p>
              Be part of the future of intelligent observability. Let's work together 
              to build more reliable, performant, and intelligent systems.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get in Touch
                <FiArrowRight />
              </Link>
              <Link to="/demo" className="btn btn-secondary">
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
